import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield, Plus, History } from 'lucide-react';
import { PERMISSIONS } from '@ooh/contracts';
import type { Role } from '@ooh/contracts';
import { Button } from '@ooh/ui/components/button';
import { InfoHint } from '@ooh/ui/components/info-hint';
import { useHasPermission } from '../../plugs/current-user';
import { QueryBoundary } from '../../layouts/query-boundary';
import { TableSkeleton } from '../../layouts/skeletons';
import { useRoleCatalog } from './use-role-catalog';
import { useRoleSidebarGroups } from './use-role-sidebar-groups';
import { useRoleCrud } from './use-role-crud';
import { resolveSelectedRole } from './resolve-selected-role';
import { IMMUTABLE_ROLE_CODES } from './role-codes';
import { RoleSidebar } from './role-sidebar';
import { RoleDetailHeader } from './role-detail-header';
import { GrantEditor } from './grant-editor';
import { RoleAuditHistoryPanel } from './role-audit-history';
import { NewRoleDialog } from './new-role-dialog';
import { ConfirmToggleActiveDialog } from './confirm-toggle-active-dialog';

export interface PermissionMatrixPageProps {
  /** Called after a grant save succeeds, e.g. so the app can refresh the signed-in user's capabilities. */
  onPermissionsSaved?: () => void;
}

interface PermissionMatrixHeaderProps {
  canViewAuditLog: boolean;
  onOpenHistory: () => void;
  onOpenNewRole: () => void;
}

function PermissionMatrixHeader({
  canViewAuditLog,
  onOpenHistory,
  onOpenNewRole,
}: PermissionMatrixHeaderProps) {
  const { t } = useTranslation('admin');
  return (
    <div className="px-8 pt-5 pb-3 border-b border-border/50 flex items-center gap-2 shrink-0">
      <Shield className="w-4 h-4 text-muted-foreground" />
      <div className="text-[17px] font-semibold tracking-tight">
        {t('permissionMatrix.header.title')}
      </div>
      <InfoHint label={t('permissionMatrix.header.infoLabel')}>
        {t('permissionMatrix.header.infoText')}
      </InfoHint>
      <div className="ml-auto flex items-center gap-2">
        {canViewAuditLog ? (
          <Button variant="outline" size="sm" onClick={onOpenHistory}>
            <History className="w-4 h-4" />
            {t('permissionMatrix.header.historyButton')}
          </Button>
        ) : null}
        <Button size="sm" onClick={onOpenNewRole} data-testid="new-role-btn">
          <Plus className="w-4 h-4" />
          {t('permissionMatrix.header.newRoleButton')}
        </Button>
      </div>
    </div>
  );
}

interface PermissionMatrixBodyProps extends PermissionMatrixPageProps {
  historyOpen: boolean;
  onCloseHistory: () => void;
  newRoleOpen: boolean;
  onCloseNewRole: () => void;
}

function PermissionMatrixBody({
  historyOpen,
  onCloseHistory,
  newRoleOpen,
  onCloseNewRole,
  onPermissionsSaved,
}: PermissionMatrixBodyProps) {
  const catalog = useRoleCatalog();
  const [selectedRoleCode, setSelectedRoleCode] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [confirmToggleOpen, setConfirmToggleOpen] = useState(false);

  const sidebarGroups = useRoleSidebarGroups(catalog.roles, searchQuery);
  const roleCrud = useRoleCrud();
  const selectedRole: Role | null = resolveSelectedRole(catalog.roles, selectedRoleCode);
  const selectedIsImmutable =
    selectedRole !== null && IMMUTABLE_ROLE_CODES.includes(selectedRole.code);
  const selectedGroup = sidebarGroups.translatedGroups.find(
    (g) => selectedRole !== null && g.roleCodes.includes(selectedRole.code),
  );

  function selectRole(code: string) {
    setSelectedRoleCode(code);
    setRenaming(false);
  }

  function handleCreateRole(name: string, code: string, groupId: string) {
    roleCrud.createRoleMutation.mutate(
      { name, code },
      {
        onSuccess: (newRole) => {
          sidebarGroups.setGroups((prev) =>
            prev.map((g) =>
              g.id === groupId ? { ...g, roleCodes: [...g.roleCodes, newRole.code] } : g,
            ),
          );
          selectRole(newRole.code);
          onCloseNewRole();
        },
      },
    );
  }

  function handleStartRename() {
    if (!selectedRole || selectedIsImmutable) return;
    setRenameValue(selectedRole.name);
    setRenaming(true);
  }

  function handleConfirmRename() {
    if (!selectedRole) return;
    const trimmed = renameValue.trim();
    if (trimmed.length === 0) {
      setRenaming(false);
      return;
    }
    roleCrud.updateRoleMutation.mutate(
      { roleId: selectedRole.id, name: trimmed },
      { onSuccess: () => setRenaming(false) },
    );
  }

  function handleConfirmToggleActive() {
    if (!selectedRole || selectedIsImmutable) return;
    roleCrud.updateRoleMutation.mutate(
      { roleId: selectedRole.id, isActive: !selectedRole.isActive },
      { onSuccess: () => setConfirmToggleOpen(false) },
    );
  }

  return (
    <>
      <div className="flex flex-1 min-h-0">
        <RoleSidebar
          roles={catalog.roles}
          groups={sidebarGroups.filteredGroups}
          selectedRoleCode={selectedRole?.code ?? ''}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onSelectRole={selectRole}
        />

        {selectedRole ? (
          <div className="flex-1 flex flex-col min-h-0 min-w-0">
            <RoleDetailHeader
              role={selectedRole}
              isImmutable={selectedIsImmutable}
              groupLabel={selectedGroup?.label}
              renaming={renaming}
              renameValue={renameValue}
              onRenameValueChange={setRenameValue}
              onStartRename={handleStartRename}
              onConfirmRename={handleConfirmRename}
              onCancelRename={() => setRenaming(false)}
              onToggleActiveClick={() => setConfirmToggleOpen(true)}
              renameSaving={roleCrud.updateRoleMutation.isPending}
            />
            <QueryBoundary
              key={selectedRole.id}
              fallback={
                <div className="flex-1 px-8 py-5">
                  <TableSkeleton rows={8} />
                </div>
              }
            >
              <GrantEditor
                role={selectedRole}
                isImmutable={selectedIsImmutable}
                catalog={catalog}
                onPermissionsSaved={onPermissionsSaved}
              />
            </QueryBoundary>
          </div>
        ) : null}
      </div>

      <RoleAuditHistoryPanel role={selectedRole} open={historyOpen} onClose={onCloseHistory} />
      <NewRoleDialog
        open={newRoleOpen}
        onClose={onCloseNewRole}
        onCreate={handleCreateRole}
        groups={sidebarGroups.translatedGroups}
        isCodeTaken={(code) => catalog.roles.some((r) => r.code === code)}
        isSubmitting={roleCrud.createRoleMutation.isPending}
      />
      {selectedRole ? (
        <ConfirmToggleActiveDialog
          open={confirmToggleOpen}
          roleName={selectedRole.name}
          activating={!selectedRole.isActive}
          onCancel={() => setConfirmToggleOpen(false)}
          onConfirm={handleConfirmToggleActive}
          isPending={roleCrud.updateRoleMutation.isPending}
        />
      ) : null}
    </>
  );
}

/**
 * Permission-matrix screen: role sidebar (search + groups), role detail panel
 * (rename, activate/deactivate, staged batch-save grant editor) and the audit
 * history slide-over. The header stays on screen while the roles load.
 */
export function PermissionMatrixPage({ onPermissionsSaved }: PermissionMatrixPageProps) {
  const { t } = useTranslation('admin');
  const canManage = useHasPermission(PERMISSIONS.ADMIN_MANAGE_ROLES);
  const canViewAuditLog = useHasPermission(PERMISSIONS.ADMIN_MANAGE_USERS);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [newRoleOpen, setNewRoleOpen] = useState(false);

  if (!canManage) {
    return (
      <div
        className="flex items-center justify-center h-full py-20"
        data-testid="permission-matrix-access-denied"
      >
        <p className="text-muted-foreground">{t('permissionMatrix.accessDenied')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <PermissionMatrixHeader
        canViewAuditLog={canViewAuditLog}
        onOpenHistory={() => setHistoryOpen(true)}
        onOpenNewRole={() => setNewRoleOpen(true)}
      />
      <QueryBoundary
        fallback={
          <div className="flex-1 p-8">
            <TableSkeleton rows={6} />
          </div>
        }
      >
        <PermissionMatrixBody
          historyOpen={historyOpen}
          onCloseHistory={() => setHistoryOpen(false)}
          newRoleOpen={newRoleOpen}
          onCloseNewRole={() => setNewRoleOpen(false)}
          onPermissionsSaved={onPermissionsSaved}
        />
      </QueryBoundary>
    </div>
  );
}
